import { useCallback, useEffect, useMemo, useState } from "react";
import { History as HistoryIcon, Loader2, Play, Trash2, X } from "lucide-react";
import { EmptyState } from "@/components/PageHeader";
import { CaptureButton } from "@/components/radio/CaptureButton";
import { StationLogo } from "@/components/radio/StationLogo";
import { usePlayer } from "@/context/PlayerContext";
import { SEARCH_SITES, searchQuery } from "@/lib/searchLinks";
import { api } from "@/lib/api";

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

const HistoryRow = ({ h, onDelete, onPlay }) => (
  <div className="group flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-zinc-50/70 sm:flex-row sm:items-center" data-testid={`history-item-${h.id}`}>
    <span className="w-14 shrink-0 font-mono text-xs text-zinc-400">{new Date(h.heard_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    <StationLogo src={h.favicon} className="h-7 w-7 shrink-0" iconSize={12} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{h.song} <span className="font-normal text-zinc-500">— {h.artist}</span></p>
      <button onClick={() => onPlay(h)} data-testid={`history-play-station-${h.id}`} className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-900"><Play size={10} />{h.station_name}</button>
    </div>
    <div className="flex items-center gap-1.5">
      {SEARCH_SITES.slice(0, 4).map((s) => (
        <a key={s.id} href={s.url(searchQuery(h.song, h.artist === "Unknown artist" ? "" : h.artist))} target="_blank" rel="noopener noreferrer"
          data-testid={`history-search-${s.id}-${h.id}`} className="rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] text-zinc-600 hover:border-zinc-900 hover:text-zinc-900">{s.label}</a>
      ))}
      <CaptureButton nowPlaying={h.title} station={h.station_name} heardAt={h.heard_at} initiallySaved={h.captured} testid={`history-capture-${h.id}`} />
      <button onClick={() => onDelete(h)} data-testid={`history-delete-${h.id}`} className="rounded p-1.5 text-zinc-300 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"><X size={14} /></button>
    </div>
  </div>
);

export const History = () => {
  const p = usePlayer();
  const [station, setStation] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/radio/history", { params: { station } }).then((r) => setData(r.data)), [station]);
  useEffect(() => { load(); }, [load, p.nowPlaying]);

  const groups = useMemo(() => {
    const out = [];
    (data?.items || []).forEach((h) => {
      const label = dayLabel(h.heard_at);
      if (out[out.length - 1]?.label !== label) out.push({ label, items: [] });
      out[out.length - 1].items.push(h);
    });
    return out;
  }, [data]);

  if (!data) return <Loader2 className="animate-spin text-zinc-400" />;
  const del = async (h) => { await api.delete(`/radio/history/${h.id}`); load(); };
  const clear = async () => {
    const name = station ? data.stations.find((s) => s.key === station)?.name : "all stations";
    if (!window.confirm(`Clear song history for ${name}?`)) return;
    await api.delete("/radio/history", { params: { station } });
    setStation("");
    load();
  };
  const play = (h) => {
    const [kind, ...rest] = h.station_key.split("-");
    const ident = rest.join("-");
    p.playStation({ ...(kind === "s" ? { id: ident } : { rb_uuid: ident }), name: h.station_name, favicon: h.favicon });
  };

  if (!data.stations.length) {
    return <EmptyState icon={HistoryIcon} title="No songs heard yet" text="Every song a station announces while you listen is logged here, so you can save ones you missed." />;
  }
  return (
    <div data-testid="radio-history">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[{ key: "", name: "All stations", count: data.stations.reduce((n, s) => n + s.count, 0) }, ...data.stations].map((s) => (
          <button key={s.key || "all"} onClick={() => setStation(s.key)} data-testid={`history-filter-${s.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${station === s.key ? "bg-zinc-900 text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-900"}`}>
            {s.name} <span className="font-mono text-[10px] opacity-60">{s.count}</span>
          </button>
        ))}
        <button onClick={clear} data-testid="history-clear-button" className="ml-auto flex items-center gap-1.5 text-xs text-zinc-500 hover:text-red-600"><Trash2 size={13} />Clear history</button>
      </div>
      {groups.map((g) => (
        <div key={g.label} className="mb-6">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{g.label}</p>
          <div className="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
            {g.items.map((h) => <HistoryRow key={h.id} h={h} onDelete={del} onPlay={play} />)}
          </div>
        </div>
      ))}
    </div>
  );
};
